import { useState } from 'react';
import { FontIcon } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { useSetAtom } from 'jotai';
import type { ICommandBarItem } from '@moko/ui';
import { successMessageAtom } from '../../atoms/messageBarAtoms';
import ConfirmDialog from '../../common/ConfirmDialog';
import type {
  IPricingParameter,
  IPricingParameterDefinition,
  PricingParameterScope,
} from '../../services/pricingParameterService';
import PricingParameterDrawer from './PricingParameterDrawer';
import PricingParameterForm from './PricingParameterForm';
import PricingParameterRecords from './PricingParameterRecords';
import {
  formatRecordValue,
  parameterLabel,
  toRecordRows,
  todayUtc,
  type ParameterSource,
} from './pricingParameterHelpers';
import { useDeletePricingParameter } from './useDeletePricingParameter';

const sourceByScope: Record<PricingParameterScope, ParameterSource> = {
  global: 'Global',
  tenant: 'Tenant',
};

export interface PricingParameterMaintenanceProps {
  scope: PricingParameterScope;
  canMutate: boolean;
  definitions: IPricingParameterDefinition[];
  pricingItemId: number;
  records: IPricingParameter[];
  loading: boolean;
  emptyTitle: string;
  emptySubtitle: string;
  onChanged: () => void;
}

const PricingParameterMaintenance = ({
  scope,
  canMutate,
  definitions,
  pricingItemId,
  records,
  loading,
  emptyTitle,
  emptySubtitle,
  onChanged,
}: PricingParameterMaintenanceProps) => {
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const [selectedRecordId, setSelectedRecordId] = useState<number>();
  const [editor, setEditor] = useState<{ record?: IPricingParameter }>();
  const [isDeleteOpen, { setTrue: showDelete, setFalse: hideDelete }] =
    useBoolean(false);

  const rows = loading ? [] : toRecordRows([[sourceByScope[scope], records]]);
  const selectedRow = rows.find((row) => row.record.id === selectedRecordId);
  const selected = selectedRow?.record;

  const deleteParameter = useDeletePricingParameter(scope, () => {
    setSelectedRecordId(undefined);
    onChanged();
  });

  const commands: ICommandBarItem[] = [
    {
      key: 'refresh',
      text: 'Refresh',
      icon: <FontIcon iconName="Refresh" />,
      onClick: onChanged,
    },
    {
      key: 'new',
      text: 'New',
      icon: <FontIcon iconName="Add" />,
      disabled: !canMutate,
      onClick: () => setEditor({}),
    },
    {
      key: 'edit',
      text: 'Edit',
      icon: <FontIcon iconName="Edit" />,
      disabled: !canMutate || !selected,
      onClick: () => setEditor({ record: selected }),
    },
    {
      key: 'delete',
      text: 'Delete',
      icon: <FontIcon iconName="Delete" />,
      disabled: !canMutate || !selected,
      onClick: showDelete,
    },
  ];

  const closeEditor = () => setEditor(undefined);

  return (
    <>
      <PricingParameterRecords
        rows={rows}
        definitions={definitions}
        loading={loading}
        today={todayUtc()}
        emptyTitle={emptyTitle}
        emptySubtitle={emptySubtitle}
        commandBarItems={commands}
        selectedId={selectedRow?.id ?? null}
        onSelectionChange={(row) => setSelectedRecordId(row.record.id)}
        onRowDoubleClicked={(row) => {
          if (canMutate) setEditor({ record: row.record });
        }}
      />
      <PricingParameterDrawer
        open={!!editor}
        title={
          editor?.record ? 'Edit pricing parameter' : 'New pricing parameter'
        }
        onClose={closeEditor}
      >
        {editor && (
          <PricingParameterForm
            scope={scope}
            pricingItemId={pricingItemId}
            definitions={definitions}
            record={editor.record}
            onClose={closeEditor}
            onSaved={(message) => {
              closeEditor();
              setSuccessMessage(message);
              onChanged();
            }}
          />
        )}
      </PricingParameterDrawer>
      <ConfirmDialog
        isModalOpen={isDeleteOpen}
        hideModal={hideDelete}
        message={
          selected
            ? `Pricing parameter ${parameterLabel(
                selected.key
              )} (${formatRecordValue(
                definitions,
                selected
              )}) would be deleted, are you sure?`
            : ''
        }
        onYesClick={() => {
          hideDelete();
          if (selected) deleteParameter(selected);
        }}
      />
    </>
  );
};

export default PricingParameterMaintenance;
